import { Logtail } from "@logtail/browser";
import config from "@/config";

/**
 * Logger service for Logtail integration
 * Provides centralized logging functionality for the application
 */
class LoggerService {
    private logtail: Logtail | null = null;
    private isInitialized = false;

    /**
     * Initialize the Logtail client
     * Should be called once at application startup
     */
    public initialize(): void {
        // Only initialize if we have a token and we're in a browser environment
        if (typeof window === "undefined") {
            return;
        }

        const sourceToken = config.LOGTAIL_SOURCE_TOKEN;
        const ingestHost = config.LOGTAIL_INGEST_HOST;

        if (!sourceToken) {
            console.warn("Logtail: Source token not configured. Logging will be disabled.");
            return;
        }

        try {
            // Logtail expects the full URL for the endpoint
            let endpoint = ingestHost || "https://in.logtail.com";

            // Validate endpoint is a URL, not accidentally a token
            if (!endpoint.startsWith("http://") && !endpoint.startsWith("https://")) {
                console.warn("⚠️ Logtail: Invalid endpoint format, using default. Got:", endpoint);
                endpoint = "https://in.logtail.com";
            }

            this.logtail = new Logtail(sourceToken, {
                endpoint: endpoint,
            });
            this.isInitialized = true;
        } catch (error) {
            console.error("Logtail: Failed to initialize", error);
        }
    }

    /**
     * Log an info message
     */
    public info(message: string, context?: Record<string, any>): void {
        if (!this.isInitialized || !this.logtail) {
            console.info(message, context);
            return;
        }

        this.logtail
            .info(message, {
                ...context,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
            })
            .catch((error) => {
                console.error("Logtail: Failed to send info log", error);
            });
    }

    /**
     * Log a warning message
     */
    public warn(message: string, context?: Record<string, any>): void {
        if (!this.isInitialized || !this.logtail) {
            console.warn(message, context);
            return;
        }

        this.logtail
            .warn(message, {
                ...context,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
            })
            .catch((error) => {
                console.error("Logtail: Failed to send warn log", error);
            });
    }

    /**
     * Log an error message
     */
    public error(message: string, error?: Error | unknown, context?: Record<string, any>): void {
        if (!this.isInitialized || !this.logtail) {
            console.error(message, error, context);
            return;
        }

        const errorContext: Record<string, any> = {
            ...context,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
        };

        if (error instanceof Error) {
            errorContext.error = {
                name: error.name,
                message: error.message,
                stack: error.stack,
            };
        } else if (error) {
            errorContext.error = String(error);
        }

        this.logtail.error(message, errorContext).catch((logError) => {
            console.error("Logtail: Failed to send error log", logError);
        });
    }

    /**
     * Log a debug message
     */
    public debug(message: string, context?: Record<string, any>): void {
        if (!this.isInitialized || !this.logtail) {
            console.debug(message, context);
            return;
        }

        this.logtail
            .debug(message, {
                ...context,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
            })
            .catch((error) => {
                console.error("Logtail: Failed to send debug log", error);
            });
    }

    /**
     * Flush all pending logs to Logtail
     * Should be called before page unload to ensure all logs are sent
     */
    public async flush(): Promise<void> {
        if (this.isInitialized && this.logtail) {
            try {
                await this.logtail.flush();
            } catch (error) {
                console.error("Logtail: Failed to flush logs", error);
            }
        }
    }
}

// Export a singleton instance
export const logger = new LoggerService();
